// File: webelieve-frontend/src/features/notes/stores/useNotesStore.ts
// Zustand store for notes state management

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Note,
  NoteFilters,
  CreateNoteDto,
  UpdateNoteDto,
  NoteType,
  NotePriority,
  NoteConfidentiality,
  NoteCasePhase,
} from '../types/note.types';
import * as notesApi from '../services/notesApi';

interface NotesState {
  // Data
  notes: Note[];
  selectedNoteId: string | null;
  total: number;
  totalPages: number;

  // UI State
  loading: boolean;
  saving: boolean;
  error: string | null;
  searchQuery: string;
  listPanelWidth: number;
  listPanelCollapsed: boolean;

  // Persisted filters
  filters: NoteFilters;

  // Actions
  fetchNotes: (scopeFilters?: Partial<NoteFilters>) => Promise<void>;
  selectNote: (noteId: string | null) => void;
  createNote: (dto: CreateNoteDto) => Promise<Note | null>;
  updateNote: (noteId: string, dto: UpdateNoteDto) => Promise<Note | null>;
  patchLocalNote: (noteId: string, patch: Partial<Note>) => void;
  deleteNote: (noteId: string) => Promise<boolean>;
  pinNote: (noteId: string, isPinned: boolean) => Promise<void>;
  searchNotes: (query: string, scopeFilters?: Partial<NoteFilters>) => Promise<void>;
  setFilter: <K extends keyof NoteFilters>(key: K, value: NoteFilters[K]) => void;
  clearFilters: () => void;
  setSearchQuery: (query: string) => void;
  setListPanelWidth: (width: number) => void;
  setListPanelCollapsed: (collapsed: boolean) => void;
  clearError: () => void;
}

const DEFAULT_FILTERS: NoteFilters = {
  sort_by: 'updated_at',
  sort_order: 'DESC',
  page: 1,
  limit: 50,
};

export const useNotesStore = create<NotesState>()(
  persist(
    (set, get) => ({
      // Initial state
      notes: [],
      selectedNoteId: null,
      total: 0,
      totalPages: 0,
      loading: false,
      saving: false,
      error: null,
      searchQuery: '',
      listPanelWidth: 350,
      listPanelCollapsed: false,
      filters: { ...DEFAULT_FILTERS },

      fetchNotes: async (scopeFilters?: Partial<NoteFilters>) => {
        set({ loading: true, error: null });
        try {
          const mergedFilters = { ...get().filters, ...scopeFilters };
          const result = await notesApi.fetchNotes(mergedFilters);
          set({
            notes: result.notes,
            total: result.total,
            totalPages: result.total_pages,
            loading: false,
          });
        } catch (err: any) {
          set({ error: err?.message || 'Failed to fetch notes', loading: false });
        }
      },

      selectNote: (noteId: string | null) => {
        set({ selectedNoteId: noteId });
      },

      createNote: async (dto: CreateNoteDto) => {
        set({ saving: true, error: null });
        try {
          const note = await notesApi.createNote(dto);
          set((state) => ({
            notes: [note, ...state.notes],
            total: state.total + 1,
            selectedNoteId: note.note_id,
            saving: false,
          }));
          return note;
        } catch (err: any) {
          set({ error: err?.message || 'Failed to create note', saving: false });
          return null;
        }
      },

      updateNote: async (noteId: string, dto: UpdateNoteDto) => {
        set({ saving: true, error: null });
        try {
          const updated = await notesApi.updateNote(noteId, dto);
          set((state) => ({
            notes: state.notes.map((n) => (n.note_id === noteId ? updated : n)),
            saving: false,
          }));
          return updated;
        } catch (err: any) {
          set({ error: err?.message || 'Failed to update note', saving: false });
          return null;
        }
      },

      patchLocalNote: (noteId: string, patch: Partial<Note>) => {
        set((state) => ({
          notes: state.notes.map((note) => (
            note.note_id === noteId
              ? { ...note, ...patch }
              : note
          )),
        }));
      },

      deleteNote: async (noteId: string) => {
        set({ error: null });
        try {
          await notesApi.deleteNote(noteId);
          set((state) => ({
            notes: state.notes.filter((n) => n.note_id !== noteId),
            total: state.total - 1,
            selectedNoteId: state.selectedNoteId === noteId ? null : state.selectedNoteId,
          }));
          return true;
        } catch (err: any) {
          set({ error: err?.message || 'Failed to delete note' });
          return false;
        }
      },

      pinNote: async (noteId: string, isPinned: boolean) => {
        try {
          const updated = await notesApi.pinNote(noteId, isPinned);
          set((state) => ({
            notes: state.notes.map((n) => (n.note_id === noteId ? updated : n)),
          }));
        } catch (err: any) {
          set({ error: err?.message || 'Failed to pin note' });
        }
      },

      searchNotes: async (query: string, scopeFilters?: Partial<NoteFilters>) => {
        set({ loading: true, error: null, searchQuery: query });
        try {
          if (!query.trim()) {
            // If query is empty, fall back to normal fetch
            await get().fetchNotes(scopeFilters);
            return;
          }
          const result = await notesApi.searchNotes(query, scopeFilters);
          set({
            notes: result.notes,
            total: result.total,
            totalPages: 1,
            loading: false,
          });
        } catch (err: any) {
          set({ error: err?.message || 'Search failed', loading: false });
        }
      },

      setFilter: (key, value) => {
        set((state) => ({
          filters: { ...state.filters, [key]: value, page: 1 },
        }));
      },

      clearFilters: () => {
        set({ filters: { ...DEFAULT_FILTERS }, searchQuery: '' });
      },

      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },

      setListPanelWidth: (width: number) => {
        set({ listPanelWidth: width });
      },

      setListPanelCollapsed: (collapsed: boolean) => {
        set({ listPanelCollapsed: collapsed });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'webelieve-notes-preferences',
      partialize: (state) => ({
        filters: state.filters,
        listPanelWidth: state.listPanelWidth,
        listPanelCollapsed: state.listPanelCollapsed,
      }),
    }
  )
);
